import { createGlobalStyle } from 'styled-components';

// Industry design system (blueprint wireframe), accent re-keyed to brand yellow.
// Text on yellow is always ink; never yellow text on paper (use accent700/800).
export const colors = {
  bg: '#f2f2f3',
  surface: '#e9e9ea',
  ink: '#1d1f20',
  divider: 'rgba(29, 31, 32, 0.16)',
  accent: '#EEB916',
  accent100: '#fdf6dc',
  accent200: '#fbeab0',
  accent300: '#f6d974',
  accent400: '#f1c63e',
  accent600: '#d4a614',
  accent700: '#8f6f06',
  accent800: '#5f4a05',
  accent900: '#2f2504',
  neutral100: '#f5f5f8',
  neutral200: '#e7e7ea',
  neutral300: '#d4d4d7',
  neutral400: '#b7b7ba',
  neutral500: '#98989b',
  neutral600: '#7a7a7d',
  neutral700: '#5d5d60',
  neutral800: '#424244',
  neutral900: '#2b2b2d',
};

// Each family has a metric-matched local fallback (size/ascent/descent overrides measured
// against Arial) so text wraps the same before and after Barlow loads: no layout shift on swap.
export const fonts = {
  heading: '"Barlow Condensed", "Barlow Condensed Fallback", system-ui, sans-serif',
  body: '"Barlow", "Barlow Fallback", system-ui, sans-serif',
};

export const theme = {
  colors,
  fonts,
  containerPad: 'clamp(16px, 4vw, 48px)',
  containerMax: '1360px',
  navHeight: 65,
  mobileBreak: 900,
  shadowLg: '0 12px 32px rgba(43, 43, 45, 0.22)',
  ease: 'cubic-bezier(.2,.7,.2,1)',
};

// Shorthand for media queries in styled-components templates.
export const mobile = `@media (max-width: ${theme.mobileBreak - 0.02}px)`;
export const desktop = `@media (min-width: ${theme.mobileBreak}px)`;

export const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Barlow Condensed Fallback';
    src: local('Arial Bold'), local('Arial-BoldMT'), local('Helvetica Bold'), local('Helvetica-Bold');
    font-weight: 600;
    size-adjust: 66.4%;
    ascent-override: 150.6%;
    descent-override: 30.1%;
    line-gap-override: 0%;
  }
  @font-face {
    font-family: 'Barlow Fallback';
    src: local('Arial'), local('ArialMT'), local('Helvetica');
    font-weight: 400 500;
    size-adjust: 96.3%;
    ascent-override: 103.8%;
    descent-override: 20.8%;
    line-gap-override: 0%;
  }
  @font-face {
    font-family: 'Barlow Fallback';
    src: local('Arial Bold'), local('Arial-BoldMT'), local('Helvetica Bold'), local('Helvetica-Bold');
    font-weight: 700;
    size-adjust: 92.1%;
    ascent-override: 108.6%;
    descent-override: 21.7%;
    line-gap-override: 0%;
  }

  *, *::before, *::after { box-sizing: border-box; }
  html { -webkit-text-size-adjust: 100%; }
  html, body { margin: 0; background: ${colors.bg}; }
  body {
    overflow-x: clip;
    color: ${colors.ink};
    font-family: ${fonts.body};
    font-size: 15px;
    line-height: 1.55;
    font-weight: 400;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  h1, h2, h3, h4, h5, h6 {
    font-family: ${fonts.heading};
    font-weight: 600;
    line-height: 1.12;
    letter-spacing: -0.015em;
    margin: 0;
  }
  p { margin: 0; text-wrap: pretty; }
  a { color: ${colors.ink}; text-underline-offset: 3px; }
  a:hover { color: ${colors.accent700}; }
  img { display: block; max-width: 100%; }
  figure { margin: 0; }
  button { font: inherit; }
  :focus { outline: none; }
  :focus-visible { outline: 2px solid ${colors.accent}; outline-offset: 2px; }
  ::selection { background: rgba(238, 185, 22, 0.3); }

  html.lenis, html.lenis body { height: auto; }
  .lenis.lenis-smooth { scroll-behavior: auto !important; }
  .lenis.lenis-stopped { overflow: hidden; }

  .skip-link {
    position: fixed; left: 12px; top: -100px; z-index: 200;
    background: ${colors.accent}; color: ${colors.ink}; padding: 10px 16px;
    font-family: ${fonts.heading}; font-weight: 600; text-transform: uppercase; text-decoration: none;
  }
  .skip-link:focus { top: 12px; }

  .visually-hidden {
    position: absolute !important; width: 1px; height: 1px; padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
  }
`;
